"use client";

import { useEffect, useRef, useState } from "react";

interface LabelState {
  text: string;
  entryId?: string;
  top?: number;
  bottom?: number;
  right: number;
}

const SCROLL_DEBOUNCE_MS = 150;
const POSITION_OFFSET = 6;

function resolvePosition(rect: DOMRect): Pick<LabelState, "top" | "bottom"> {
  if (rect.top < 0) {
    return { top: rect.bottom + POSITION_OFFSET, bottom: undefined };
  }
  return {
    top: undefined,
    bottom: window.innerHeight - rect.top + POSITION_OFFSET,
  };
}

function resolveLabel(
  fieldId: string,
  el: HTMLElement,
  cache: Map<string, string>
): Pick<LabelState, "text" | "entryId"> {
  const entryId = el.dataset.contentfulEntryId;
  if (!entryId) {
    return { text: fieldId };
  }
  const cached = cache.get(entryId);
  if (cached) {
    return { text: cached };
  }
  return { text: "", entryId };
}

export function PreviewInspectorLabel() {
  const [label, setLabel] = useState<LabelState | null>(null);
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentTypeCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!label?.entryId) {
      return;
    }
    const entryId = label.entryId;

    const cached = contentTypeCache.current.get(entryId);
    if (cached) {
      setLabel((prev) =>
        prev?.entryId === entryId
          ? { ...prev, text: cached, entryId: undefined }
          : prev
      );
      return;
    }

    let cancelled = false;
    fetch(`/api/preview/content-type?entryId=${entryId}`)
      .then((r) => r.json())
      .then(({ internalName }: { internalName: string | null }) => {
        if (cancelled || !internalName) {
          return;
        }
        contentTypeCache.current.set(entryId, internalName);
        setLabel((prev) =>
          prev?.entryId === entryId
            ? { ...prev, text: internalName, entryId: undefined }
            : prev
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLabel((prev) =>
            prev?.entryId === entryId ? { ...prev, entryId: undefined } : prev
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [label?.entryId]);

  useEffect(() => {
    function labelFromElement(el: HTMLElement | null): LabelState | null {
      let currentElement = el;

      while (currentElement) {
        const fieldId = currentElement.dataset.contentfulFieldId;
        if (fieldId) {
          const rect = currentElement.getBoundingClientRect();
          return {
            ...resolveLabel(fieldId, currentElement, contentTypeCache.current),
            ...resolvePosition(rect),
            right: window.innerWidth - rect.right,
          };
        }
        currentElement = currentElement.parentElement;
      }
      return null;
    }

    function handleMouseOver(e: MouseEvent) {
      lastMousePos.current = { x: e.clientX, y: e.clientY };
      setLabel(labelFromElement(e.target as HTMLElement));
    }

    function handleScroll() {
      setLabel(null);
      if (scrollTimer.current) {
        clearTimeout(scrollTimer.current);
      }
      scrollTimer.current = setTimeout(() => {
        if (!lastMousePos.current) {
          return;
        }
        const el = document.elementFromPoint(
          lastMousePos.current.x,
          lastMousePos.current.y
        ) as HTMLElement | null;
        setLabel(labelFromElement(el));
      }, SCROLL_DEBOUNCE_MS);
    }

    document.addEventListener("mouseover", handleMouseOver);
    window.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener("mouseover", handleMouseOver);
      window.removeEventListener("scroll", handleScroll, { capture: true });
      if (scrollTimer.current) {
        clearTimeout(scrollTimer.current);
      }
    };
  }, []);

  if (!label?.text) {
    return null;
  }

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed z-[9999] rounded-sm bg-blue-600 px-2 py-1 font-mono text-[11px] text-white shadow"
      style={{ top: label.top, bottom: label.bottom, right: label.right }}
    >
      {label.text}
    </div>
  );
}
